import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPermissions, GetPermissionsResponse } from 'src/api/getPermissions';
import { buildPermissionSections } from 'src/components/RoleModal/permissions';
import { QueryKeys } from 'src/constants/QueryKeys';

// Permissions rarely change, so keep them fresh for a day to avoid refetching
// on every remount or window focus, and retain them in the cache for the session.
const PERMISSIONS_STALE_TIME = 1000 * 60 * 60 * 24;

// Error notification is left to the page-level consumer: useQuery's onError
// fires once per mounted observer, so notifying here duplicates snackbars
// whenever the modal and the page observe the same failure.
export const usePermissionCatalogue = () => {
  const { data, isLoading, isError, error } = useQuery<GetPermissionsResponse, Error>({
    queryKey: [QueryKeys.PERMISSIONS],
    queryFn: getPermissions,
    staleTime: PERMISSIONS_STALE_TIME,
    cacheTime: Infinity,
    retry: false,
  });

  const catalogue = data?.data;
  const sections = useMemo(
    () =>
      catalogue
        ? buildPermissionSections(
            catalogue.applications,
            catalogue.collections,
            catalogue.permissions,
          )
        : [],
    [catalogue],
  );

  return { catalogue, sections, isLoading, isError, error };
};
